import { memo, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Popup, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet/dist/leaflet.css";
import HeartButton from "./HeartButton";
import { useFavorites } from "../context/FavoritesContext";
import { KIND_LABEL } from "../lib/discoveryApi";
import { Hand, Check, LocateFixed, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const BARN_ICON_URL = "https://customer-assets.emergentagent.com/job_farm-market-27/artifacts/4f78y3qm_64358B7F-123D-4BDB-9623-B02D21805007.png";
// Blank wooden roadside sign (inline SVG, intentionally no text or symbols).
const SIGN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
<rect x="29" y="30" width="6" height="31" rx="1.5" fill="#7a5230"/><rect x="29" y="30" width="2" height="31" fill="#946640" opacity=".6"/>
<path d="M27 60h10" stroke="#5e3d22" stroke-width="2.5" stroke-linecap="round"/>
<rect x="6" y="11" width="52" height="24" rx="3" fill="#b07a45" stroke="#6b4426" stroke-width="2.5"/>
<path d="M8 19.5h48M8 27h48" stroke="#8d5c33" stroke-width="1.4" opacity=".8"/>
<path d="M14 15.5c6 1.2 11-.8 17 0M34 23c5 1 10-.6 16 .2M12 31c7 .8 12-.6 18 .2" stroke="#c8925a" stroke-width="1" fill="none" opacity=".9" stroke-linecap="round"/>
<circle cx="11" cy="15" r="1.3" fill="#5e3d22"/><circle cx="53" cy="15" r="1.3" fill="#5e3d22"/><circle cx="11" cy="31" r="1.3" fill="#5e3d22"/><circle cx="53" cy="31" r="1.3" fill="#5e3d22"/>
</svg>`;
export const SIGN_ICON_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(SIGN_SVG)}`;
export const CARROT_ICON_URL = "https://customer-assets.emergentagent.com/job_farm-market-27/artifacts/njlloc24_422BA362-655A-4E23-8918-6AFE358E17D1.png";
const INK = '#0f1d2f';
const GOLD = '#B6A46D';
const S = 26;
const IS_TOUCH = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches;

// Distinct, on-brand marker per kind (shared by single markers and clusters).
export const KIND_ICON_HTML = {
  farm: `<img src="${BARN_ICON_URL}" style="width:${S}px;height:${S}px;object-fit:contain" />`,
  farmstand: `<img src="${SIGN_ICON_URL}" alt="" style="width:${S - 5}px;height:${S - 5}px;object-fit:contain" />`,
  market: `<img src="${CARROT_ICON_URL}" style="width:${S}px;height:${S}px;object-fit:contain" />`,
  seller: `<span style="display:flex;align-items:center;justify-content:center;width:${S + 6}px;height:${S + 6}px;border-radius:50%;background:${INK};border:2px solid ${GOLD};box-shadow:0 1px 4px rgba(0,0,0,.35)"><img src="${BARN_ICON_URL}" style="width:18px;height:18px;object-fit:contain;filter:brightness(0) invert(1)" /></span>`,
};
const icons = Object.fromEntries(Object.entries(KIND_ICON_HTML).map(([k, html]) => [k, L.divIcon({ html, className: "af-pin", iconSize: [S + 6, S + 6], iconAnchor: [(S + 6) / 2, S + 6], popupAnchor: [0, -S] })]));
const clusterFns = Object.fromEntries(Object.entries(KIND_ICON_HTML).map(([k, html]) => [k, (c) => L.divIcon({
  html: `<span style="position:relative;display:inline-block">${html}<b style="position:absolute;top:-6px;right:-10px;background:${INK};color:#fff;font:600 10px/16px Arial;min-width:16px;padding:0 4px;border-radius:8px;text-align:center">${c.getChildCount()}</b></span>`,
  className: "af-pin", iconSize: [S + 6, S + 6] })]));
const clusterRadius = (z) => (z <= 4 ? 80 : z <= 7 ? 50 : 20);
const CLUSTER_OPTS = { chunkedLoading: true, chunkInterval: 120, chunkDelay: 30, maxClusterRadius: clusterRadius, spiderfyOnMaxZoom: true, showCoverageOnHover: false, disableClusteringAtZoom: 10, animate: false, removeOutsideVisibleBounds: true };
const POPUP_PROPS = { maxWidth: 270, autoPanPaddingTopLeft: [16, 110], autoPanPaddingBottomRight: [16, 16] };

const Label = ({ kind }) => (
  <p className="text-[10px] tracking-widest uppercase mb-2" style={{ color: kind === "seller" ? '#8f7d45' : INK, opacity: kind === "seller" ? 1 : 0.55 }} data-testid="map-popup-label">{KIND_LABEL[kind]}</p>
);

export const ACCESS_NOTICE = "Public access unconfirmed—contact the farm first.";
export const AccessNotice = ({ testid, className = "" }) => (
  <p className={`text-xs px-2 py-1.5 border-l-2 ${className}`} style={{ color: INK, borderColor: '#b6a46d', backgroundColor: 'rgba(182,164,109,0.12)' }} data-testid={testid}>{ACCESS_NOTICE}</p>
);

export const APPROX_NOTICE = "Approximate location (ZIP area)—contact the farm for directions.";
export const ApproxNotice = ({ testid, className = "" }) => (
  <p className={`text-xs px-2 py-1.5 border-l-2 ${className}`} style={{ color: INK, borderColor: 'rgba(15,29,47,0.35)', backgroundColor: 'rgba(15,29,47,0.05)' }} data-testid={testid}>{APPROX_NOTICE}</p>
);

export const MAP_RETURN_KEY = "af_map_return";
const DETAIL_PATH = { market: "/marketplace/markets/", seller: "/marketplace/farms/", farm: "/marketplace/listings/", farmstand: "/marketplace/listings/" };

// One compact popup for every kind: label, heart, name, location, open details.
function PopupBody({ item }) {
  const fav = useFavorites();
  const saved = item.kind === "market" ? fav.isMarketFavorite(item.id) : item.kind === "seller" ? fav.isFarmFavorite(item.id) : fav.isPlaceFavorite(item.id);
  const toggle = () => item.kind === "market"
    ? fav.toggleFavoriteMarket(item.id, { name: item.name, city: item.city, state: item.state })
    : item.kind === "seller"
      ? fav.toggleFavoriteFarm(item.id, { farm_name: item.name, city: item.city, state: item.state })
      : fav.toggleFavoritePlace({ id: item.id, name: item.name, city: item.city, state: item.state, category: item.kind });
  const testid = item.kind === "market" ? "map-popup-market" : item.kind === "seller" ? "map-popup-seller" : "map-popup-directory";
  return (
    <div style={{ minWidth: 200 }} data-testid={testid}>
      <div className="flex justify-between items-start gap-2">
        <Label kind={item.kind} />
        <HeartButton freeAllowed isFavorite={saved} onToggle={toggle} size="sm" />
      </div>
      <h3 className="text-base mb-1 font-medium break-words" style={{ color: INK }}>{item.name}</h3>
      <p className="text-xs mb-3" style={{ color: INK, opacity: 0.6 }}>{[item.city, item.state].filter(Boolean).join(", ")}</p>
      {item.approx && <ApproxNotice testid="map-popup-location-approx" className="mb-2" />}
      {item.accessUnconfirmed && <AccessNotice testid="map-popup-access-unconfirmed" className="mb-3" />}
      <Link to={`${DETAIL_PATH[item.kind]}${encodeURIComponent(item.id)}`} state={{ from: "map" }}
        onClick={() => sessionStorage.setItem(MAP_RETURN_KEY, "1")}
        className="inline-block px-4 py-2 text-xs tracking-widest uppercase bg-[#0f1d2f]" style={{ color: '#fff' }}
        data-testid={item.kind === "seller" ? "map-popup-view-farm" : item.kind === "market" ? "map-popup-view-market" : "map-popup-view-details"}>
        {item.kind === "seller" ? "View Farm" : item.kind === "market" ? "View Market" : "View Details"}
      </Link>
    </div>
  );
}

function ClusterLayer({ points, kind, onSelect }) {
  const map = useMap();
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;
  useEffect(() => {
    const group = L.markerClusterGroup({ ...CLUSTER_OPTS, iconCreateFunction: clusterFns[kind] });
    group.addLayers(points.map((p) => L.marker([p.lat, p.lng], { icon: icons[kind], keyboard: false, zIndexOffset: kind === "seller" ? 1000 : 0 }).on("click", () => selectRef.current(p))));
    map.addLayer(group);
    return () => { map.removeLayer(group); group.clearLayers(); };
  }, [points, kind, map]);
  return null;
}

function SelectedPopup({ selected, onClose }) {
  if (!selected) return null;
  return (
    <Popup key={`${selected.kind}-${selected.id}`} position={[selected.lat, selected.lng]} offset={[0, -S + 4]} eventHandlers={{ remove: onClose }} {...POPUP_PROPS}>
      <PopupBody item={selected} />
    </Popup>
  );
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    if (target.bounds) map.flyToBounds(target.bounds, { padding: [30, 30], maxZoom: 12, duration: 1 });
    else map.flyTo([target.lat, target.lng], target.zoom || 10, { duration: 1 });
  }, [target, map]);
  return null;
}

function Legend({ kinds }) {
  const map = useMap();
  const [zoom, setZoom] = useState(map.getZoom());
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });
  const label = zoom <= 4 ? "Across the nation" : zoom <= 7 ? "Regional view" : zoom <= 10 ? "Exploring locally" : "Full detail";
  const names = { farm: "Farms", farmstand: "Farmstands", market: "Markets", seller: "Sellers" };
  return (
    <div className="absolute bottom-3 left-3 bg-white/95 px-3 py-2 shadow-sm pointer-events-none" style={{ zIndex: 500 }} data-testid="map-legend">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ color: INK }}>
        {kinds.map((k) => <span key={k} className="inline-flex items-center gap-1"><span className="inline-flex scale-[0.7] origin-left -mr-1.5" dangerouslySetInnerHTML={{ __html: KIND_ICON_HTML[k] }} />{names[k]}</span>)}
      </div>
      <p className="text-[10px] mt-0.5" style={{ color: INK, opacity: 0.45 }}>{label}</p>
    </div>
  );
}

// Touch: one-finger swipes scroll the page until the map is tapped once; then one-finger panning.
function TouchActivation() {
  const map = useMap();
  const [active, setActive] = useState(false);
  const pillRef = useRef(null);
  useEffect(() => {
    if (!IS_TOUCH) return;
    if (active) map.dragging.enable(); else map.dragging.disable();
  }, [active, map]);
  useMapEvents({ click: () => { if (IS_TOUCH && !active) setActive(true); } });
  useEffect(() => {
    if (!IS_TOUCH || !active) return;
    const el = map.getContainer();
    const io = new IntersectionObserver(([e]) => { if (!e.isIntersecting) setActive(false); }, { threshold: 0.2, rootMargin: "-80px 0px 0px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [active, map]);
  useEffect(() => { if (pillRef.current) L.DomEvent.disableClickPropagation(pillRef.current); });
  if (!IS_TOUCH) return null;
  return active ? (
    <button ref={pillRef} type="button" onClick={() => setActive(false)}
      className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-widest uppercase shadow-md"
      style={{ zIndex: 600, backgroundColor: INK, color: 'white' }} data-testid="map-touch-done">
      <Check className="w-3.5 h-3.5" style={{ color: GOLD }} /> Exploring map · Done
    </button>
  ) : (
    <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-wide bg-white/95 shadow-sm pointer-events-none"
      style={{ zIndex: 600, color: INK }} data-testid="map-touch-hint">
      <Hand className="w-3.5 h-3.5" style={{ color: GOLD }} /> Tap map to explore
    </div>
  );
}

function NearMe({ onLocate }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (ref.current) { L.DomEvent.disableClickPropagation(ref.current); L.DomEvent.disableScrollPropagation(ref.current); }
  }, []);
  const locate = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available on this device."); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => { setBusy(false); onLocate({ lat: coords.latitude, lng: coords.longitude }); },
      (err) => {
        setBusy(false);
        toast.error(err.code === 1 ? "Location permission is off. Enable it in your browser settings, or search by city or ZIP." : "We couldn't find your location. Try searching by city or ZIP.");
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  };
  return (
    <button ref={ref} type="button" onClick={locate} disabled={busy} aria-label="Center map near me"
      className="absolute left-3 inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-widest uppercase bg-white shadow-md border disabled:opacity-60"
      style={{ zIndex: 600, top: 88, color: INK, borderColor: 'rgba(15,29,47,0.2)' }} data-testid="map-near-me-btn">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LocateFixed className="w-3.5 h-3.5" style={{ color: GOLD }} />} Near me
    </button>
  );
}

function ViewTracker({ onView }) {
  const map = useMap();
  useMapEvents({ moveend: () => { const c = map.getCenter(); onView?.({ center: [c.lat, c.lng], zoom: map.getZoom() }); } });
  return null;
}

function ExploreMap({ layers, flyTarget, area, selected, onSelect, onLocate, initialView, onView }) {
  const kinds = Object.keys(layers).filter((k) => layers[k].length > 0);
  return (
    <MapContainer center={initialView?.center || [39.8283, -98.5795]} zoom={initialView?.zoom || 4} style={{ height: '100%', width: '100%' }}
      scrollWheelZoom={!IS_TOUCH} dragging={!IS_TOUCH} zoomSnap={0.5} worldCopyJump={false} preferCanvas>
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" updateWhenZooming={false} keepBuffer={3} />
      <FlyTo target={flyTarget} />
      <ViewTracker onView={onView} />
      <TouchActivation />
      <NearMe onLocate={onLocate} />
      <Legend kinds={kinds} />
      {area && <Circle center={[area.lat, area.lng]} radius={area.radius * 1609.34} pathOptions={{ color: GOLD, weight: 1.5, fillOpacity: 0.04, interactive: false }} />}
      {kinds.map((k) => <ClusterLayer key={k} points={layers[k]} kind={k} onSelect={onSelect} />)}
      <SelectedPopup selected={selected} onClose={() => onSelect(null)} />
    </MapContainer>
  );
}

export default memo(ExploreMap);
